import React, { useState, useCallback, useRef, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import tarefasService from 'src/services/tarefasService';
import AgenteIaPainel from './AgenteIaPainel';
import AcompanhamentoPanel from '../AcompanhamentoPanel';
import LeadSearch from '../orcamento/LeadSearch';
import { formatCurrency } from '../orcamento/OrcamentoItens';
import { calcularTotais, addDias, itensPreenchidos } from '../orcamento/orcamentoModel';
import { getIniciais } from '../Propostas.design';
import { DOCUMENTO_DESIGN as D } from '../EditorDocumento.design';

const FORMATOS = [
  { valor: 'blocos', label: 'Blocos' },
  { valor: 'canvas', label: 'Design livre' }
];

// "20 set 2026": curto o bastante para não quebrar na coluna de 320px.
const formatDataCurta = (d) => new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' }).format(d).replace(/\./g, '').replace(/ de /g, ' ');

function Secao({ titulo, children }) {
  return (
    <section className={D.sideSection}>
      {titulo && <h3 className={`${D.sideTitle} mb-2`}>{titulo}</h3>}
      {children}
    </section>
  );
}

// Linha de um grupo: ícone, rótulo, valor à direita e o controle (link, chevron,
// switch). Clicável quando `onClick` existe.
function LinhaGrupo({ icon, rotulo, valor, vazio, acao, onClick, controle, disabled }) {
  const conteudo = (
    <>
      {icon && <span className={D.groupIcon}><Iconify icon={icon} width={15} /></span>}
      <span className={D.groupLabel}>{rotulo}</span>
      <span className={valor ? D.groupValue : D.groupValueMuted} title={valor || undefined}>{valor || vazio}</span>
      {acao && <span className={`${D.link} flex-shrink-0`}>{acao}</span>}
      {controle}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={`${D.groupRow} ${D.groupRowBtn}`} onClick={onClick} disabled={disabled}>
        {conteudo}
      </button>
    );
  }
  return <div className={D.groupRow}>{conteudo}</div>;
}

function Switch({ ligado, onChange, disabled, rotulo }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      disabled={disabled}
      onClick={onChange}
      className={`${D.switchTrack} ${ligado ? 'bg-[#7C3AED] justify-end' : 'bg-gray-300 dark:bg-[#383838] justify-start'} disabled:opacity-50`}
    >
      <span className={D.switchKnob} />
    </button>
  );
}

// Painel lateral do editor de proposta: grupos de linhas com controles (cliente,
// documento, opções, resumo) numa área que rola, e o agente de IA num rodapé
// fixo. Com `chatAberto`, a conversa toma o painel inteiro.
export default function PropostaSidebar({
  selectedLead, onSelectLead, selectedTarefa, onSelectTarefa, chatVinculado, readOnly, status,
  onGerarComIa, mensagensIa, chatAberto, onToggleChat, propostaId,
  assinatura, assinaturaEditavel = true, onChangeAssinatura,
  contato, onChangeContato,
  templates, selectedTemplate, onSelectTemplate, onCreateNewTemplate,
  designMode, onToggleDesignMode,
  itens, descontoGlobalPercent, validadeDias, createdAt
}) {
  const { user } = useAuth();

  const [modeloAberto, setModeloAberto] = useState(false);
  const [editandoContato, setEditandoContato] = useState(false);
  const [vinculandoTarefa, setVinculandoTarefa] = useState(false);
  const [tarefaSearch, setTarefaSearch] = useState('');
  const [tarefaResults, setTarefaResults] = useState([]);
  const [tarefaLoading, setTarefaLoading] = useState(false);
  const tarefaDebounceRef = useRef(null);
  const modeloRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (modeloRef.current && !modeloRef.current.contains(e.target)) setModeloAberto(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setModeloAberto(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const searchTarefas = useCallback((termo) => {
    if (tarefaDebounceRef.current) clearTimeout(tarefaDebounceRef.current);
    if (!termo || termo.length < 2) { setTarefaResults([]); return; }
    setTarefaLoading(true);
    tarefaDebounceRef.current = setTimeout(async () => {
      try {
        const res = await tarefasService.listar(user?.clienteId, { search: termo, limit: 10 });
        setTarefaResults(res.data || []);
      } catch (err) {
        console.error('Erro ao pesquisar tarefas:', err);
        setTarefaResults([]);
      } finally {
        setTarefaLoading(false);
      }
    }, 300);
  }, [user?.clienteId]);

  const chatDisponivel = Boolean(onGerarComIa && (!readOnly || mensagensIa?.length > 0));
  const totalMensagens = mensagensIa?.length || 0;

  // Modo conversa: só o chat, ocupando o painel inteiro.
  if (chatAberto && chatDisponivel) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-200/70 dark:border-[#383838] flex-shrink-0">
          <div>
            <h3 className={`${D.sideTitle} flex items-center gap-1.5`}>
              <Iconify icon="mdi:creation" width={14} className="text-[#7C3AED]" />
              Agente de IA
            </h3>
            <p className={D.sideHelp}>Descreva a proposta e o agente monta o documento.</p>
          </div>
          <button type="button" onClick={() => onToggleChat(false)} title="Voltar ao resumo" aria-label="Fechar conversa" className={D.iconBtn}>
            <Iconify icon="eva:close-outline" width={16} />
          </button>
        </div>
        <div className="flex-1 min-h-0 flex flex-col px-5 pb-4 pt-3">
          <AgenteIaPainel
            mensagens={mensagensIa}
            readOnly={readOnly}
            chatVinculado={chatVinculado}
            selectedLead={selectedLead}
            onEnviar={onGerarComIa}
            autoFocus
          />
        </div>
      </div>
    );
  }

  const exigeAssinatura = Boolean(assinatura?.exigir);
  const alterarAssinatura = (mudanca) => onChangeAssinatura?.({ ...(assinatura || {}), ...mudanca });
  const temModelos = Boolean(templates && templates.length > 0);
  const temDocumento = temModelos || Boolean(onToggleDesignMode);
  const { total } = calcularTotais(itens, descontoGlobalPercent);
  const qtdItens = itensPreenchidos(itens).length;
  const validoAte = formatDataCurta(addDias(createdAt, validadeDias || 15));
  const c = contato || {};
  const contatoResumo = [c.email, c.telefone].filter(Boolean).join(' · ');
  const temCliente = Boolean(selectedLead || c.nome);

  const camposContato = (
    <div className={D.groupDetail}>
      <div className={D.gridStack}>
        {!selectedLead && (
          <label className={D.field}>
            <span className={D.label}>Nome</span>
            <input className={D.input} value={c.nome || ''} onChange={e => onChangeContato?.({ nome: e.target.value })} placeholder="Nome do cliente" autoFocus />
          </label>
        )}
        <label className={D.field}>
          <span className={D.label}>E-mail</span>
          <input className={D.input} type="email" value={c.email || ''} onChange={e => onChangeContato?.({ email: e.target.value })} placeholder="email@empresa.com" autoFocus={Boolean(selectedLead)} />
        </label>
        <label className={D.field}>
          <span className={D.label}>Telefone</span>
          <input className={D.input} value={c.telefone || ''} onChange={e => onChangeContato?.({ telefone: e.target.value })} placeholder="(00) 00000-0000" />
        </label>
      </div>
      <button type="button" className={`${D.btn} ${D.btnGhost} ${D.btnSmall} mt-3`} onClick={() => setEditandoContato(false)}>
        Concluir
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
    <div className={`${D.sidebarScroll} flex-1 min-h-0`}>

      {/* ---------- Cliente ---------- */}
      <Secao titulo="Cliente">
        {!temCliente && !readOnly && !editandoContato && (
          <div className="space-y-2">
            <LeadSearch onSelect={onSelectLead} />
            <button type="button" className={D.emptyCard} onClick={() => setEditandoContato(true)}>
              <span className={D.emptyIcon}><Iconify icon="mdi:account-plus-outline" width={18} /></span>
              <span className="min-w-0 flex-1">
                <span className={`${D.personName} block`}>Nenhum cliente ainda</span>
                <span className={`${D.personMeta} block`}>Ou preencha à mão</span>
              </span>
              <Iconify icon="eva:chevron-right-fill" width={16} className="text-gray-400 flex-shrink-0" />
            </button>
          </div>
        )}

        {(temCliente || editandoContato || readOnly) && (
          <div className={D.group}>
            {selectedLead ? (
              <div className={D.groupRow}>
                <div className={D.avatar}>{getIniciais(selectedLead.nome)}</div>
                <div className="flex-1 min-w-0">
                  <p className={D.personName}>{selectedLead.nome}</p>
                  <p className={D.personMeta}>
                    Lead do CRM{selectedLead.valor !== undefined && selectedLead.valor !== null ? ` · ${formatCurrency(selectedLead.valor)}` : ''}
                  </p>
                </div>
                {!readOnly && (
                  <button type="button" className={D.link} onClick={() => onSelectLead(null)}>trocar</button>
                )}
              </div>
            ) : (
              <div className={D.groupRow}>
                <div className={D.avatar}>{c.nome ? getIniciais(c.nome) : <Iconify icon="mdi:account-outline" width={16} />}</div>
                <div className="flex-1 min-w-0">
                  <p className={c.nome ? D.personName : `${D.personMeta} italic`}>{c.nome || 'Sem nome'}</p>
                  <p className={D.personMeta}>Contato avulso</p>
                </div>
                {!readOnly && !editandoContato && (
                  <button type="button" className={D.link} onClick={() => setEditandoContato(true)}>editar</button>
                )}
              </div>
            )}

            {!editandoContato && (
              <LinhaGrupo
                icon="mdi:email-outline"
                rotulo="Contato"
                valor={contatoResumo}
                vazio="Sem e-mail ou telefone"
                acao={!readOnly ? 'editar' : undefined}
                onClick={!readOnly ? () => setEditandoContato(true) : undefined}
              />
            )}
            {editandoContato && !readOnly && camposContato}
          </div>
        )}

        {chatVinculado && (
          <p className={D.wa}>
            <span className={D.waDot} />
            Conversa do WhatsApp vinculada. A proposta aparece no chat ao ser criada.
          </p>
        )}
      </Secao>

      {/* ---------- Documento ---------- */}
      {temDocumento && (
        <Secao titulo="Documento">
          <div className="relative" ref={modeloRef}>
            <div className={D.group}>
              {temModelos && (
                <LinhaGrupo
                  icon="mdi:file-multiple-outline"
                  rotulo="Modelo"
                  valor={selectedTemplate?.nome}
                  vazio="Escolher modelo"
                  onClick={!readOnly ? () => setModeloAberto(v => !v) : undefined}
                  controle={!readOnly && (
                    <Iconify icon={modeloAberto ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={16} className="text-gray-400 flex-shrink-0" />
                  )}
                />
              )}
              {onToggleDesignMode && (
                <div className={`${D.groupRow} flex-wrap`}>
                  <span className={D.groupIcon}><Iconify icon="mdi:view-agenda-outline" width={15} /></span>
                  <span className={D.groupLabel}>Formato</span>
                  <span className={D.groupValueMuted}>{designMode === 'canvas' ? 'Arrastar e soltar' : 'Seções em sequência'}</span>
                  <div className={`${D.segment} w-full h-8 mb-1`} role="group" aria-label="Formato do editor">
                    {FORMATOS.map(({ valor, label }) => {
                      const ativo = designMode === valor;
                      return (
                        <button
                          key={valor}
                          type="button"
                          aria-pressed={ativo}
                          disabled={readOnly}
                          onClick={() => { if (!ativo) onToggleDesignMode(); }}
                          className={`${D.segmentBtn} px-2 ${ativo ? D.segmentBtnOn : D.segmentBtnOff}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {modeloAberto && (
              <div className={D.popDown} role="dialog" aria-label="Escolher modelo">
                <p className={D.popTitle}>Modelo</p>
                <p className={D.popText}>Define aparência e conteúdo inicial. Trocar substitui o conteúdo atual.</p>
                <div className="space-y-0.5 max-h-56 overflow-y-auto">
                  {templates.map(t => (
                    <button
                      key={t._id}
                      type="button"
                      className={`${D.popOption} ${t._id === selectedTemplate?._id ? D.popOptionAtivo : ''}`}
                      onClick={() => { setModeloAberto(false); onSelectTemplate?.(t); }}
                    >
                      <span className="truncate">{t.nome}</span>
                      {t.padrao && <span className="text-[10px] font-semibold uppercase text-[#7C3AED] flex-shrink-0">Padrão</span>}
                    </button>
                  ))}
                  {onCreateNewTemplate && (
                    <button
                      type="button"
                      className={`${D.popOption} !text-[#7C3AED] font-medium`}
                      onClick={() => { setModeloAberto(false); onCreateNewTemplate(); }}
                    >
                      <span className="flex items-center gap-1.5"><Iconify icon="eva:plus-fill" width={14} />Novo modelo de proposta</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Secao>
      )}

      {/* ---------- Opções ---------- */}
      <Secao titulo="Opções">
        <div className={D.group}>
          {selectedTarefa ? (
            <LinhaGrupo
              icon="mdi:clipboard-check-outline"
              rotulo="Tarefa"
              valor={selectedTarefa.title}
              acao={!readOnly ? 'remover' : undefined}
              onClick={!readOnly ? () => onSelectTarefa(null) : undefined}
            />
          ) : (
            <LinhaGrupo
              icon="mdi:clipboard-check-outline"
              rotulo="Tarefa"
              vazio={vinculandoTarefa ? 'Buscando…' : 'Nenhuma'}
              acao={!readOnly && !vinculandoTarefa ? 'vincular' : undefined}
              onClick={!readOnly && !vinculandoTarefa ? () => setVinculandoTarefa(true) : undefined}
            />
          )}
          {vinculandoTarefa && !selectedTarefa && (
            <div className={D.groupDetail}>
              <div className={D.search}>
                <Iconify icon={tarefaLoading ? 'mdi:loading' : 'eva:search-outline'} width={16} className={`${D.searchIcon} ${tarefaLoading ? 'animate-spin' : ''}`} />
                <input
                  value={tarefaSearch}
                  onChange={e => { setTarefaSearch(e.target.value); searchTarefas(e.target.value); }}
                  placeholder="Buscar tarefa pelo título"
                  className={`${D.input} pl-9 bg-white dark:bg-[#222529]`}
                  aria-label="Buscar tarefa"
                  autoFocus
                />
                {tarefaResults.length > 0 && (
                  <div className={D.dropdown}>
                    {tarefaResults.map(tarefa => (
                      <button
                        key={tarefa._id}
                        type="button"
                        onClick={() => { onSelectTarefa(tarefa); setTarefaSearch(''); setTarefaResults([]); setVinculandoTarefa(false); }}
                        className={D.dropdownItem}
                      >
                        <Iconify icon="mdi:clipboard-check-outline" width={15} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <span className={D.personName}>{tarefa.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" className={`${D.link} mt-2`} onClick={() => { setVinculandoTarefa(false); setTarefaSearch(''); setTarefaResults([]); }}>cancelar</button>
            </div>
          )}

          {onChangeAssinatura && (
            <>
              <div className={D.groupRow}>
                <span className={D.groupIcon}><Iconify icon="mdi:draw-pen" width={15} /></span>
                <span className="flex-1 min-w-0">
                  <span className={`${D.groupLabel} block`}>Assinatura do cliente</span>
                  <span className={`${D.personMeta} block`}>{exigeAssinatura ? 'Exigida para aceitar' : 'Aceite sem assinatura'}</span>
                </span>
                <Switch
                  ligado={exigeAssinatura}
                  disabled={!assinaturaEditavel}
                  rotulo="Exigir assinatura do cliente"
                  onChange={() => alterarAssinatura({ exigir: !exigeAssinatura })}
                />
              </div>
              {exigeAssinatura && (
                <div className={D.groupDetail}>
                  <label className={D.field}>
                    <span className={D.label}>Termo de aceite</span>
                    <textarea
                      value={assinatura?.texto_termo || ''}
                      onChange={e => alterarAssinatura({ texto_termo: e.target.value })}
                      disabled={!assinaturaEditavel}
                      rows={3}
                      placeholder="Declaro que li e concordo com os termos, valores e condições descritos nesta proposta."
                      className={`${D.textarea} bg-white dark:bg-[#222529]`}
                    />
                  </label>
                  <div className="mt-2.5 space-y-2">
                    {[
                      { chave: 'exigir_documento', label: 'Pedir CPF ou CNPJ', ativo: assinatura?.exigir_documento !== false },
                      { chave: 'exigir_email', label: 'Pedir e-mail', ativo: Boolean(assinatura?.exigir_email) }
                    ].map(item => (
                      <label key={item.chave} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.ativo}
                          disabled={!assinaturaEditavel}
                          onChange={e => alterarAssinatura({ [item.chave]: e.target.checked })}
                          className={D.checkbox}
                        />
                        <span className="text-[13px] text-gray-700 dark:text-[#ABABAD]">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Secao>

      {/* ---------- Resumo ---------- */}
      <Secao titulo="Resumo">
        <div className={D.group}>
          <div className={D.groupRow}>
            <span className={D.groupLabel}>Total</span>
            <span className={`${D.groupValue} !text-base !font-bold`}>{formatCurrency(total)}</span>
          </div>
          <LinhaGrupo rotulo="Itens" valor={qtdItens ? `${qtdItens} ${qtdItens === 1 ? 'item' : 'itens'}` : undefined} vazio="Nenhum" />
          <LinhaGrupo rotulo="Válido até" valor={validoAte} />
        </div>
      </Secao>

      {propostaId && status && status !== 'rascunho' && (
        <Secao titulo="Acompanhamento">
          <AcompanhamentoPanel propostaId={propostaId} />
        </Secao>
      )}

      {status && status !== 'rascunho' && (
        <div className="px-5 py-4">
          <p className={D.sideHelp}>Esta proposta já foi enviada e não pode mais ser editada.</p>
        </div>
      )}
    </div>

    {chatDisponivel && (
      <div className={D.sidebarFooter}>
        <button type="button" onClick={() => onToggleChat(true)} className={`${D.btn} ${D.btnPrimary} ${D.btnBlock}`}>
          <Iconify icon="mdi:creation" width={16} />
          {totalMensagens > 0 ? 'Continuar com o agente' : 'Conversar com o agente'}
          {totalMensagens > 0 && <span className={D.agentBadge}>{totalMensagens}</span>}
        </button>
      </div>
    )}
    </div>
  );
}
